import className from "classnames/bind";
import useRouter from "@/hooks/useRouter";
import { useEffect, useMemo, useState } from "react";
import { AnimatePresence } from "motion/react";
import { useSearch } from "@/providers/SearchContext";
import { GetPageCategoriesQuery, GetSearchPageQuery } from "@graphqlTypes";
import { interleaveResults } from "@/utils/interleaveResults";
import { updateBodyScroll } from "@/core/utils";
import SearchForm from "./SearchForm";
import RockHallResults from "./RockHallResults";

import styles from "./Search.module.scss";
import SearchLoader from "./SearchLoader";
const cx = className.bind(styles);

type SearchProps = {
  page: GetSearchPageQuery['rhPage']['searchPageData'];
  databaseId: number;
  categories: GetPageCategoriesQuery['pageCategories']['nodes'];
  pressReleaseSearchImage?: AcfMediaFragment;
}

const resultsVariants = {
  initial: { opacity: 0, filter: "blur(10px)" },
  animate: { opacity: 1, filter: "blur(0px)" },
  loading: { opacity: 0.25, filter: "blur(0px)", pointerEvents: "none" },
  exit: { opacity: 0, filter: "blur(10px)" },
  transition: { duration: 0.5 },
};

export default function Search({ page, categories, databaseId, pressReleaseSearchImage }: SearchProps) {
  const {
    fetchSearchResults,
    results,
    setResults,
    lastSearchTerm,
    currentPage,
    loading,
    resultsCount,
    perPage,
    setHasSearched,
    hasSearched,
    searchPath,
    setIgnoreSearch,
    setCurrentPage,
    setExclude,
    ignoreSearch,
    resetSearch,
    setIsSearchPage,
    shopResults,
    setShopResults,
    termCounts,
  } = useSearch();
  const { shopCarousel, noResults, noShopResults, searchPlaceholder, resultsText, rockHallLabel, shopLabel, brandFilterLabel } = page;

  const [searchTerm, setSearchTerm] = useState("");
  const [typeFilters, setTypeFilters] = useState<string[]>([]);
  const router = useRouter();

  const hasNonShopFilters = typeFilters.some((t) => t !== "shop");
  const hasShopFilter = typeFilters.includes("shop");
  const showShop = typeFilters.length === 0 || hasShopFilter;
  const showWp = typeFilters.length === 0 || hasNonShopFilters;

  const mergedResults = useMemo(
    () => interleaveResults(showWp ? results : [], showShop ? shopResults : []),
    [results, shopResults, showWp, showShop]
  );

  const augmentedTermCounts = useMemo(
    () => [...(termCounts || []), { termSlug: "shop", count: shopResults.length }],
    [termCounts, shopResults]
  );
  const totalPages = Math.ceil(mergedResults.length / perPage);
  const showPaging = totalPages > 1;
  const pageResults = useMemo(
    () => mergedResults.slice((currentPage - 1) * perPage, currentPage * perPage),
    [mergedResults, currentPage, perPage]
  );

  const handleFormSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (searchTerm) {
      setCurrentPage(1);
      router.push({
        pathname: searchPath,
        query: {
          s: searchTerm,
        }
      }, { shallow: true });
    }
  };

  const onTypeFilterClick = (type: string) => {
    setTypeFilters((prev) => {
      if (prev.includes(type)) {
        return prev.filter((t) => t !== type);
      }
      return [...prev, type];
    });
  };

  const onSearchClear = () => {
    setSearchTerm("");
    setHasSearched(false);
    setResults([]);
    setShopResults([]);
    router.push({ pathname: "search" }, { shallow: true });
  };

  useEffect(() => {
    setExclude([databaseId]);
    setIsSearchPage(true);
    updateBodyScroll(false);
    return () => {
      resetSearch();
      setIsSearchPage(false);
    }
  }, []);

  useEffect(() => {
    if (typeFilters.length > 0) {
      const currentTypes = router.query.types ? (typeof router.query.types === "string" ? router.query.types.split(',') : []) : [];
      const typesString = typeFilters.join(',');
      if (typesString !== currentTypes.join(',')) {
        setCurrentPage(1);
        router.push({
          pathname: searchPath,
          query: {
            s: searchTerm,
            types: typesString
          }
        }, { shallow: true });
      }
    } else if (searchTerm) {
      router.push({
        pathname: searchPath,
        query: {
          s: searchTerm,
        }
      }, { shallow: true } );
    }
  }, [typeFilters]);

  useEffect(() => {
    if (currentPage > 1) {
      router.push({
        pathname: searchPath,
        query: {
          s: searchTerm,
          ...(typeFilters.length > 0 && { types: typeFilters.join(',') }),
          page: currentPage
        }
      }, { shallow: true });
    } else if (searchTerm) {
      router.push({
        pathname: searchPath,
        query: {
          s: searchTerm,
          ...(typeFilters.length > 0 && { types: typeFilters.join(',') }),
        }
      }, { shallow: true } );
    }
  }, [currentPage]);

  useEffect(() => {
    if (router.isReady) {
      const { s, types } = router.query;
      if (typeof s === "string" && s.length > 0 && !ignoreSearch) {
        const typeArray = typeof types === "string" ? types.split(',') : [];
        const typeIds = categories.filter(cat => typeArray.includes(cat.slug)).map(cat => cat.databaseId);
        const page = typeof router.query.page === "string" ? parseInt(router.query.page) : 1;
        if (page && page !== currentPage) setCurrentPage(page);

        fetchSearchResults(s, typeIds);
        if (JSON.stringify(typeArray) !== JSON.stringify(typeFilters)) {
          setTypeFilters(typeArray);
        }
        if (s !== lastSearchTerm) {
          setSearchTerm(s);
        }
      } else if (ignoreSearch) {
        if (typeFilters.length > 0) setTypeFilters([]);
        setSearchTerm(lastSearchTerm);
        setIgnoreSearch(false);
      } else if (s !== undefined && !s.length) {
        router.push({ pathname: searchPath }, { shallow: true });
      }
    }
  }, [router.query]);

  return (
    <>
      <SearchForm
        setSearchTerm={setSearchTerm}
        handleFormSubmit={handleFormSubmit}
        searchTerm={searchTerm}
        placeholder={searchPlaceholder}
        onSearchClear={onSearchClear}
      />
        <div className={cx("search-container")}>
          <AnimatePresence>
            { hasSearched && (loading) && (
              <SearchLoader
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                className="hasSearched"
                transition={{ duration: 0.35, ease: "easeOut" }}
              />
            )}
          </AnimatePresence>
          <AnimatePresence mode="wait">
            { !hasSearched && loading && (
              <SearchLoader
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.35, ease: "easeOut" }}
              />
            )}
            {hasSearched ? (
              <>
                <RockHallResults
                  initial={resultsVariants.initial}
                  animate={loading ? resultsVariants.loading : resultsVariants.animate}
                  exit={resultsVariants.exit}
                  headline={rockHallLabel}
                  pressReleaseSearchImage={pressReleaseSearchImage}
                  categories={categories}
                  searchTerm={lastSearchTerm}
                  onTypeFilterClick={onTypeFilterClick}
                  typeFilters={typeFilters}
                  transition={resultsVariants.transition}
                  brandFilterLabel={brandFilterLabel}
                  termCounts={augmentedTermCounts}
                  noResults={noResults}
                  pageResults={pageResults}
                  key="rockhall-results"
                  totalCount={mergedResults.length}
                  showPaging={showPaging}
                />
              </>
            ) : null}
          </AnimatePresence>
        </div>
    </>
  );
}
